import { createClient } from '@supabase/supabase-js';

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Supabase credentials not found. Google OAuth will not work. ' +
    'Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables.'
  );
}

// Create the Supabase client (will be null if credentials missing)
export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

/**
 * Sign in with Google OAuth
 * @returns Promise with URL to redirect to, or error
 */
export async function signInWithGoogle(): Promise<{ url: string | null; error: Error | null }> {
  if (!supabase) {
    return { 
      url: null, 
      error: new Error('Supabase not configured. Please set environment variables.') 
    };
  }

  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      return { url: null, error };
    }

    return { url: data.url, error: null };
  } catch (e) {
    return { 
      url: null, 
      error: e instanceof Error ? e : new Error('Failed to initiate Google sign-in') 
    };
  }
}

/**
 * Handle OAuth callback - exchange code for session
 * @returns Session tokens if successful
 */
export async function handleOAuthCallback(): Promise<{
  accessToken: string | null;
  refreshToken: string | null;
  error: Error | null;
}> {
  if (!supabase) {
    return { 
      accessToken: null, 
      refreshToken: null, 
      error: new Error('Supabase not configured') 
    };
  }

  try {
    // Supabase automatically handles the code exchange when getSession is called
    // after the OAuth redirect
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      return { accessToken: null, refreshToken: null, error };
    }

    if (data.session) {
      return {
        accessToken: data.session.access_token,
        refreshToken: data.session.refresh_token,
        error: null,
      };
    }

    return { 
      accessToken: null, 
      refreshToken: null, 
      error: new Error('No session found after OAuth callback') 
    };
  } catch (e) {
    return { 
      accessToken: null, 
      refreshToken: null, 
      error: e instanceof Error ? e : new Error('Failed to handle OAuth callback') 
    };
  }
}
